import { ComponentProps, ComponentType, createElement, lazy, ReactNode, useState } from "react";
import { BlockFailed, BlockLoading, DialogFailed, DialogLoading, PartFailure, PartHost } from "./parts";

/** Fetches the part's module and names the component in it: `() => import("./X").then((m) => ({ default: m.X }))`. */
type Loader<C> = () => Promise<{ default: C }>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyPart = ComponentType<any>;

type Shows<P> = {
  /** While the part's code is on its way. */
  loading: (props: P) => ReactNode;
  /** The part could not be shown: why (code did not arrive / did not arrive again / crashed) and Try again. */
  failed: (props: P, failure: PartFailure) => ReactNode;
  /** Nothing is asked for (or shown) before this is first true; after, the part stays (a dialog animates closed). */
  wanted?: (props: P) => boolean;
};

/**
 * A part of a page kept out of the main bundle (2D F12 follow-up): its code is fetched the first time it is wanted.
 * A failed fetch is not remembered: Try again fetches it again. A part that crashes while drawing is caught too (there is
 * no app-wide catch), logged, and offered only Close.
 */
export function lazyPart<C extends AnyPart>(load: Loader<C>, shows: Shows<ComponentProps<C>>): ComponentType<ComponentProps<C>> {
  type P = ComponentProps<C>;
  let Part = lazy(load);
  const Lazy = (props: P) => {
    const want = shows.wanted?.(props) ?? true;
    const [asked, setAsked] = useState(want);
    const [, setAttempt] = useState(0);
    // Each new opening (wanted again after not) starts with a fresh catch: a part that crashed is drawn again.
    const [opening, setOpening] = useState({ want, n: 0 });
    if (want !== opening.want) setOpening({ want, n: want ? opening.n + 1 : opening.n });
    if (want && !asked) setAsked(true);
    if (!asked) return null;
    const again = () => { Part = lazy(load); setAttempt((n) => n + 1); };
    return createElement(PartHost, {
      key: opening.n, part: Part as unknown as ComponentType<object>, props, loading: shows.loading(props), failed: (f) => shows.failed(props, f), onRetry: again,
    });
  };
  return Lazy;
}

type DialogProps = { open: boolean; onClose: () => void };
type DialogOf<C extends AnyPart> = ComponentProps<C> extends DialogProps ? ComponentType<ComponentProps<C>> : never;

/**
 * A dialog kept out of the main bundle: fetched on its first open, a quiet loading dialog meanwhile (after 250 ms). A component that is
 * not a dialog (no `open` / `onClose`) gets `never`, so using it does not compile.
 */
export function lazyDialog<C extends AnyPart>(load: Loader<C>): DialogOf<C> {
  const d = (p: ComponentProps<C>) => p as DialogProps;
  return lazyPart<C>(load, {
    wanted: (p) => d(p).open,
    loading: (p) => createElement(DialogLoading, { open: d(p).open, onClose: d(p).onClose }),
    failed: (p, failure) => createElement(DialogFailed, { open: d(p).open, onClose: d(p).onClose, failure }),
  }) as DialogOf<C>;
}

/** The same for a part shown inside a panel or a page: the kit's loading lines, then its error state. */
export function lazyBlock<C extends AnyPart>(load: Loader<C>, opts: { rows?: number } = {}): ComponentType<ComponentProps<C>> {
  return lazyPart<C>(load, {
    loading: () => createElement(BlockLoading, { rows: opts.rows ?? 3 }),
    failed: (_p, failure) => createElement(BlockFailed, { failure }),
  });
}
